import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Checkbox,
    FormGroup,
    Box,
    Chip,
    TextField,
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useParams } from 'react-router-dom';
import getAuthToken from '../../utils/authToken';
import TaskStatusModal from '../outputJson/taskStatusModal';
import { constants } from '../../utils/constants';

interface ExportTaskDialogProps {
    open: boolean;
    onClose: () => void;
}

const ExportTaskDialog: React.FC<ExportTaskDialogProps> = ({ open, onClose }) => {
    const [exportType, setExportType] = useState<'complete' | 'custom'>('complete');
    const [customStages, setCustomStages] = useState<any[]>([]);
    const [selectedStages, setSelectedStages] = useState<any[]>([]);
    const [includeInputData, setIncludeInputData] = useState(false);
    const [autoRefillName, setAutoRefillName] = useState(false);
    const [openTaskStatusModal, setOpenTaskStatusModal] = useState(false);
    const [taskStatusData, setTaskStatusData] = useState<any>(null);
    const { id } = useParams();

    // Fetch stages for custom export when dialog opens
    useEffect(() => {
        if (open && id) {
            const token = getAuthToken();
            fetch(`${constants.API_BASE_URL}project/company/stage/?project_id=${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            })
                .then(res => res.json())
                .then(data => {
                    if (data.results) {
                        setCustomStages(data.results);
                    }
                })
                .catch(err => console.error('Failed to fetch stages:', err));
        }
    }, [open, id]);

    const handleClose = () => {
        setExportType('complete');
        setCustomStages([]);
        setSelectedStages([]);
        setIncludeInputData(false);
        setAutoRefillName(false);
        setOpenTaskStatusModal(false);
        setTaskStatusData(null);
        onClose();
    };

    const pollTaskStatus = async (taskId: string, token: string) => {
        const maxAttempts = 60; // Poll for up to 5 minutes (60 * 5 seconds)
        let attempts = 0;

        const checkStatus = async (): Promise<void> => {
            try {
                const response = await fetch(
                    `${constants.API_BASE_URL}project/company/project/output-json/tasks/status?celery_task_id=${taskId}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );

                const result = await response.json();
                
                // Update task status data for modal
                setTaskStatusData(result.data);

                if (result.data?.status === 'SUCCESS' && result.data?.url) {
                    // Download the file from S3 URL
                    const s3Url = result.data.url;
                    const urlParts = s3Url.split('/');
                    const filename = urlParts[urlParts.length - 1] || `export_${new Date().getTime()}.xlsx`;
                    
                    const fileResponse = await fetch(s3Url);
                    const blob = await fileResponse.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                } else if (result.data?.status === 'FAILURE') {
                    // Modal will show failure status
                } else {
                    // Still processing, poll again
                    attempts++;
                    if (attempts < maxAttempts) {
                        setTimeout(checkStatus, 5000); // Poll every 5 seconds
                    } else {
                        setTaskStatusData({ status: 'FAILURE' });
                    }
                }
            } catch (error) {
                console.error('Status check failed:', error);
                setTaskStatusData({ status: 'FAILURE' });
            }
        };

        checkStatus();
    };

    const handleSubmit = async () => {
        const token = getAuthToken();
        setTaskStatusData({ status: 'PENDING' });
        setOpenTaskStatusModal(true);
        const inputDataRequired = includeInputData.toString();
        const autoFillReview = autoRefillName.toString();
        
        try {
            let apiUrl = '';
            
            if (exportType === 'complete') {
                const queryParams = new URLSearchParams({
                    project_id: id || '',
                    input_data_required: inputDataRequired,
                    auto_fill_review: autoFillReview,
                });
                apiUrl = `${constants.API_BASE_URL}task/company/data-dump/?${queryParams.toString()}`;
            } else {
                const stageIds = selectedStages.map(stage => stage.id);
                const stagesListParam = JSON.stringify(stageIds);
                const queryParams = new URLSearchParams({
                    project_id: id || '',
                    stages_list: stagesListParam,
                    input_data_required: inputDataRequired,
                    auto_fill_review: autoFillReview,
                });
                apiUrl = `${constants.API_BASE_URL}task/company/data-dump/?${queryParams.toString()}`;
            }

            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            
            if (response.ok) {
                const result = await response.json();
                
                if (result.data?.task_id) {
                    // Start polling for task status
                    pollTaskStatus(result.data.task_id, token);
                } else {
                    setTaskStatusData({ status: 'FAILURE' });
                }
            } else {
                setTaskStatusData({ status: 'FAILURE' });
            }
        } catch (error) {
            console.error('Export failed:', error);
            setTaskStatusData({ status: 'FAILURE' });
        }
    };

    return (
        <>
        <Dialog open={open} fullWidth maxWidth="md">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingRight: "20px" }}>
                <DialogTitle>Export Completed Tasks</DialogTitle>
                <ClearIcon onClick={handleClose} style={{ cursor: "pointer" }} />
            </div>
            <DialogContent dividers>
                {/* Export Type Selection */}
                <Box component="fieldset" sx={{ mb: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
                    <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600, fontFamily: "montserrat" }}>
                        Export Type :
                    </FormLabel>
                    <RadioGroup value={exportType} onChange={(e: any) => setExportType(e.target.value)}>
                        <FormControlLabel 
                            value="complete" 
                            control={<Radio />} 
                            label={<span>Complete</span>} 
                        />
                        <FormControlLabel 
                            value="custom" 
                            control={<Radio />} 
                            label={<span>Custom (Stages)</span>} 
                        />
                    </RadioGroup>
                </Box>

                {/* Include Input Data for Complete Export */}
                {exportType === 'complete' && (
                    <Box component="fieldset" sx={{ mb: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={includeInputData}
                                        onChange={(e) => setIncludeInputData(e.target.checked)}
                                    />
                                }
                                label={<span>Include Input Data?</span>}
                            />
                            <FormControlLabel
                                sx={{ mt: 1 }}
                                control={
                                    <Checkbox
                                        checked={autoRefillName}
                                        onChange={(e) => setAutoRefillName(e.target.checked)}
                                    />
                                }
                                label={<span>Fallback Tasker Value</span>}
                            />
                        </Box>
                    </Box>
                )}

                {/* Custom Stages Selection */}
                {exportType === 'custom' && (
                    <Box component="fieldset" sx={{ mb: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
                        <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600, fontFamily: "montserrat" }}>
                            Select Stages :
                        </FormLabel>
                        <Autocomplete
                            multiple
                            options={customStages}
                            getOptionLabel={(option) => option.name}
                            value={selectedStages}
                            onChange={(event, newValue) => {
                                setSelectedStages(newValue);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Select stages"
                                    size="small"
                                />
                            )}
                            renderOption={(props, option) => (
                                <Box
                                    component="li"
                                    {...props}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        width: '100%',
                                    }}
                                >
                                    <span>{option.name}</span>
                                    <Chip
                                        size="small"
                                        sx={{
                                            fontSize: "11px",
                                            backgroundColor: option.category === "REVIEW" ? "#1976d2" : "#f0f0f0",
                                            color: option.category === "REVIEW" ? "#ffffff" : "#000000",
                                            border: "1px solid #ccc",
                                            ml: 1
                                        }}
                                        label={option.category || "Task"}
                                    />
                                </Box>
                            )}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={option.id}
                                        label={option.name}
                                        size="small"
                                        sx={{
                                            backgroundColor: option.category === "REVIEW" ? "#1976d2" : "#f0f0f0",
                                            color: option.category === "REVIEW" ? "#ffffff" : "#000000",
                                        }}
                                    />
                                ))
                            }
                        />
                        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={includeInputData}
                                        onChange={(e) => setIncludeInputData(e.target.checked)}
                                    />
                                }
                                label={<span>Include Input Data?</span>}
                            />
                            <FormControlLabel
                                sx={{ mt: 1 }}
                                control={
                                    <Checkbox
                                        checked={autoRefillName}
                                        onChange={(e) => setAutoRefillName(e.target.checked)}
                                    />
                                }
                                label={<span>Fallback Tasker Value</span>}
                            />
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={openTaskStatusModal}
                >
                    Download
                </Button>
            </DialogActions>
        </Dialog>

        {/* Processing Modal */}
        <TaskStatusModal
            open={openTaskStatusModal}
            onClose={() => {
                setOpenTaskStatusModal(false);
                setTaskStatusData(null);
                handleClose(); 
            }}
            taskStatusData={taskStatusData}
            completedTaskExported={true}
        />
        </>
    );
};

export default ExportTaskDialog;
